// What only a live editor on the screen adds, by profile. The server never
// loads these: it needs only the schema and the markdown (schema.ts).

import type { AnyExtension } from "@tiptap/core";
import type { Transaction } from "@tiptap/pm/state";
import { BlockFocusPlugin } from "./block-focus-plugin";
import { BlockIdPlugin, isYSyncTransaction } from "./block-id-plugin";
import { FootnoteBlocks } from "./footnotes";
import { KeepViewOnRemote } from "./keep-view-plugin";
import { ListBlockPlugin } from "./list-blocks";
import { MarkdownLengthGuard } from "./markdown-length-guard";
import type { EditorProfile } from "./schema";
import { SpellcheckPlugin } from "./spellcheck-plugin";

export interface LiveExtensionOptions {
  profile: EditorProfile;
  /** Which transactions are someone else's (default: y-sync transactions). */
  isRemote?: (tr: Transaction) => boolean;
  /** "note": the longest markdown the person may type (none by default). */
  maxLength?: number;
}

/**
 * - "document": one block per list item (before ids, so a split is renumbered),
 *   footnotes gathered at the end, block ids, the keyboard-focus outline, spell
 *   check on the current block, a still view on remote changes.
 * - "note": spell check, a still view, and the length limit.
 */
export function liveEditorExtensions(
  options: LiveExtensionOptions,
): AnyExtension[] {
  const isRemote = options.isRemote ?? isYSyncTransaction;
  const keepView = KeepViewOnRemote.configure({ isRemote });
  if (options.profile === "document") {
    return [
      ListBlockPlugin,
      FootnoteBlocks,
      BlockIdPlugin,
      BlockFocusPlugin,
      SpellcheckPlugin,
      keepView,
    ];
  }
  return [
    SpellcheckPlugin,
    keepView,
    ...(options.maxLength === undefined
      ? []
      : [MarkdownLengthGuard.configure({ max: options.maxLength, isRemote })]),
  ];
}
